import React, { useMemo, useContext, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { selectBoothById, DesignCanvasLayer } from 'models/cms/booths';
import { BoothContext } from 'pages/booths/BoothProfileContainer';
import ContentAreaElement from './ContentAreaElement';
import ImageElement from './ImageElement';
import ColorFillElement from './ColorFillElement';
import ImageButtonElement from './ImageButtonElement';
import NetworkingWidgetElement from './NetworkingWidgetElement';

interface GenericBoothDesignLayerProps {
  layer: DesignCanvasLayer;
  boothId: string;
  canvasAspectRatio: number;
  isPreview: boolean;
  boothVideoAutoplay: boolean;
}

interface LayerElementType {
  LayerElementComponent: (props: any) => React.ReactElement<any>;
}

const GenericBoothDesignLayer: React.FC<GenericBoothDesignLayerProps> = ({
  layer, boothId, canvasAspectRatio, isPreview, boothVideoAutoplay,
}: GenericBoothDesignLayerProps) => {
  const {
    __typename: typeName, name,
  } = layer;

  const layerTypeMap: { [typename: string]: LayerElementType } = {
    BoothDesignContentAreaElement: { LayerElementComponent: ContentAreaElement },
    BoothDesignImageElement: { LayerElementComponent: ImageElement },
    BoothDesignColorFillElement: { LayerElementComponent: ColorFillElement },
    BoothDesignImageButtonElement: { LayerElementComponent: ImageButtonElement },
    BoothDesignNetworkingWidgetElement: { LayerElementComponent: NetworkingWidgetElement },
  };

  const LayerComponent = useMemo(() => layerTypeMap[typeName]?.LayerElementComponent, [typeName]);
  const booth = useSelector((state) => selectBoothById(state, boothId));

  const boothForMatomo = useContext(BoothContext);

  const onElementClick = useCallback(() => {
    if (name) {
      window.trackingItemToFirebase(null, 'Booth', 'Banner', `${name}_Click`, name, booth.id, boothForMatomo.boothNameForMatomo);
    }
  }, [booth.id, boothForMatomo.boothNameForMatomo, name]);

  return (
    !isEmpty(layer) && [LayerComponent, layer, booth].every(Boolean) && (
      <LayerComponent
        layer={layer}
        booth={booth}
        canvasAspectRatio={canvasAspectRatio}
        isPreview={isPreview}
        onElementClick={onElementClick}
        boothVideoAutoplay={boothVideoAutoplay}
      />
    )
  );
};

export default GenericBoothDesignLayer;
